import { computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ClassGroupQueries,
  GroupQueries,
  PersonPerRoleQueries,
  PersonPreferenceActions,
  PersonPreferenceInterface,
  PersonPreferenceQueries,
  PreferenceKey,
  StudentInterface,
} from '@campus/dal';
import { Dictionary } from '@ngrx/entity';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { select, Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

export interface AssigneeStudentInterface {
  id: string;
  label: string;
  firstName: string;
  displayName: string;
  name: string;
  avatar: string;
}

export interface GlobalAssigneeFilterStateInterface {
  selectedClassGroupIds: Dictionary<boolean>;
  selectedGroupIds: Dictionary<boolean>;
  selectedStudentIds: Dictionary<boolean>;
  isStudentDialogOpen: boolean;
  searchTerm: string;
}

const initialState: GlobalAssigneeFilterStateInterface = {
  selectedClassGroupIds: {},
  selectedGroupIds: {},
  selectedStudentIds: {},
  isStudentDialogOpen: false,
  searchTerm: '',
};

export type GlobalAssigneeFilterStore = InstanceType<typeof GlobalAssigneeFilterStore>;

export const GlobalAssigneeFilterStore = signalStore(
  { providedIn: 'root' },
  withState<GlobalAssigneeFilterStateInterface>(initialState),
  withComputed((store) => {
    const ngrxStore = inject(Store);

    const globalAssigneeFilterPreference = toSignal(
      ngrxStore.pipe(
        select(PersonPreferenceQueries.getByKey),
        map((preferencesByKey) => {
          const prefArr = preferencesByKey[PreferenceKey.GLOBAL_ASSIGNEE_FILTER];
          const value =
            prefArr && prefArr.length > 0
              ? (prefArr[0].value as { groupIds?: number[]; classgroupIds?: number[]; studentIds?: number[] })
              : { groupIds: [], classgroupIds: [], studentIds: [] };

          return {
            groupIds: value.groupIds || [],
            classgroupIds: value.classgroupIds || [],
            studentIds: value.studentIds || [],
          };
        })
      ),
      { initialValue: { groupIds: [], classgroupIds: [], studentIds: [] } }
    );

    const classGroups = toSignal(ngrxStore.select(ClassGroupQueries.getAll), {
      initialValue: [],
    });

    const groups = toSignal(ngrxStore.select(GroupQueries.getAll), {
      initialValue: [],
    });

    const students = toSignal(ngrxStore.select(PersonPerRoleQueries.getStudents), { initialValue: [] });

    const studentsByClassGroupId = toSignal(ngrxStore.select(PersonPerRoleQueries.getStudentsByClassGroupId), {
      initialValue: {} as Dictionary<StudentInterface[]>,
    });

    const studentsByGroupId = toSignal(ngrxStore.select(PersonPerRoleQueries.getStudentsByGroupId), {
      initialValue: {} as Dictionary<StudentInterface[]>,
    });

    const searchTermLower = computed(() => store.searchTerm().toLowerCase());

    const filterStudent = (student: AssigneeStudentInterface) => {
      const term = searchTermLower();
      if (!term) return true;
      return (
        (student.firstName && student.firstName.toLowerCase().includes(term)) ||
        (student.displayName && student.displayName.toLowerCase().includes(term)) ||
        (student.name && student.name.toLowerCase().includes(term))
      );
    };

    const studentsGroupedByClassGroup = computed(() => {
      const classGroupsDict = studentsByClassGroupId();
      const classGroupsArray = classGroups();

      return classGroupsArray
        .map((cg) => {
          const studentsInGroup = classGroupsDict[cg.id] || [];
          const mappedStudents = studentsInGroup.map(mapStudentToAssignee).filter(filterStudent);
          return {
            classGroupId: +cg.id,
            classGroupName: cg.name,
            students: mappedStudents,
          };
        })
        .filter((group) => group.students.length > 0);
    });

    const studentsGroupedByGroup = computed(() => {
      const groupDict = studentsByGroupId();
      const groupsArray = groups();

      return groupsArray
        .map((g) => {
          const studentsInGroup = groupDict[g.id] || [];
          const filteredStudents = studentsInGroup.map(mapStudentToAssignee).filter(filterStudent);

          return {
            groupId: +g.id,
            groupName: g.name,
            students: filteredStudents,
          };
        })
        .filter((group) => group.students.length > 0);
    });

    const assigneeStudents = computed(() => {
      return students().map(mapStudentToAssignee);
    });

    const selectedClassGroupIds = computed(() => {
      return { ...idsToDictionary(globalAssigneeFilterPreference().classgroupIds), ...store.selectedClassGroupIds() };
    });

    const selectedGroupIds = computed(() => {
      return { ...idsToDictionary(globalAssigneeFilterPreference().groupIds), ...store.selectedGroupIds() };
    });

    const selectedStudentIds = computed(() => {
      return { ...idsToDictionary(globalAssigneeFilterPreference().studentIds), ...store.selectedStudentIds() };
    });

    const selectedAssigneeNames = computed(() => {
      const selectedNames: string[] = [];

      const sClassGroupIds = selectedClassGroupIds();
      classGroups().forEach((cg) => {
        if (sClassGroupIds[cg.id]) {
          selectedNames.push(cg.name);
        }
      });

      const sGroupIds = selectedGroupIds();
      groups().forEach((g) => {
        if (sGroupIds[g.id]) {
          selectedNames.push(g.name);
        }
      });

      const sStudentIds = selectedStudentIds();
      students().forEach((s) => {
        if (sStudentIds[s.id]) {
          selectedNames.push(s.displayName || `${s.firstName} ${s.name}`);
        }
      });

      return selectedNames.join(', ');
    });

    return {
      classGroups,
      groups,
      students: assigneeStudents,
      studentsGroupedByClassGroup,
      studentsGroupedByGroup,
      selectedClassGroupIds,
      selectedGroupIds,
      selectedStudentIds,
      selectedAssigneeNames,
    };
  }),
  withMethods((store) => {
    const ngrxStore = inject(Store);

    const updatePersonPreference = () => {
      const classgroupIds = dictionaryToIds(store.selectedClassGroupIds());
      const groupIds = dictionaryToIds(store.selectedGroupIds());
      const studentIds = dictionaryToIds(store.selectedStudentIds());

      const personPreference: PersonPreferenceInterface = {
        key: PreferenceKey.GLOBAL_ASSIGNEE_FILTER,
        value: { groupIds, classgroupIds, studentIds },
      };

      ngrxStore.dispatch(
        new PersonPreferenceActions.StartUpsertPersonPreference({
          personPreference,
          noFeedBack: true,
        })
      );
    };

    return {
      toggleClassGroup(id: number, checked: boolean) {
        patchState(store, (state) => ({
          selectedClassGroupIds: {
            ...state.selectedClassGroupIds,
            [id]: checked,
          },
        }));
        updatePersonPreference();
      },
      toggleGroup(id: number, checked: boolean) {
        patchState(store, (state) => ({
          selectedGroupIds: {
            ...state.selectedGroupIds,
            [id]: checked,
          },
        }));
        updatePersonPreference();
      },
      toggleStudent(id: number, checked: boolean) {
        patchState(store, (state) => ({
          selectedStudentIds: {
            ...state.selectedStudentIds,
            [id]: checked,
          },
        }));
        updatePersonPreference();
      },
      openStudentDialog() {
        patchState(store, { isStudentDialogOpen: true });
      },
      closeStudentDialog() {
        patchState(store, { isStudentDialogOpen: false, searchTerm: '' });
      },
      setSearchTerm(term: string) {
        patchState(store, { searchTerm: term });
      },
      updateSelections(classGroupIds: Record<string, boolean>, groupIds: Record<string, boolean>) {
        patchState(store, {
          selectedClassGroupIds: classGroupIds,
          selectedGroupIds: groupIds,
        });
        updatePersonPreference();
      },
      clearSelections() {
        const clearedClassGroupIds = Object.keys(store.selectedClassGroupIds()).reduce((acc, key) => ({ ...acc, [key]: false }), {});
        const clearedGroupIds = Object.keys(store.selectedGroupIds()).reduce((acc, key) => ({ ...acc, [key]: false }), {});
        const clearedStudentIds = Object.keys(store.selectedStudentIds()).reduce((acc, key) => ({ ...acc, [key]: false }), {});

        patchState(store, {
          selectedClassGroupIds: clearedClassGroupIds,
          selectedGroupIds: clearedGroupIds,
          selectedStudentIds: clearedStudentIds,
        });
        updatePersonPreference();
      },
    };
  })
);

function mapStudentToAssignee(s: StudentInterface): AssigneeStudentInterface {
  return {
    id: s.id.toString(),
    label: s.displayName || `${s.firstName} ${s.name}`,
    firstName: s.firstName,
    displayName: s.displayName,
    name: s.name,
    avatar: s.avatar,
  };
}

function idsToDictionary(ids: number[]): Dictionary<boolean> {
  const dict: Dictionary<boolean> = {};
  ids.forEach((id) => {
    dict[id.toString()] = true;
  });
  return dict;
}

function dictionaryToIds(dict: Dictionary<boolean>): number[] {
  return Object.keys(dict)
    .filter((id) => +dict[id])
    .map((id) => +id);
}
